import { useEffect, useMemo, useState } from 'react';
import { InputAdornment, Modal, Switch } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Close } from '@mui/icons-material';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useToast } from 'use-toast-mui';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllEmployees,
  getPositionAuths,
  getUserAuths,
  savePositionAuths,
  saveUserAuths,
  applyAllPositionAuths,
  bulkSaveUserAuths,
  setEmployeePassword,
  updateEmployee,
} from '../../../api/positionAuth';

import CommonModalButton from '../../../form-components/CommonModalButton';

import {
  PageContainer,
  LeftColumn,
  RightColumn,
  SearchCard,
  SearchInput,
  ExcludeRetiredRow,
  ExcludeRetiredLabel,
  RoleListWrapper,
  RoleAccordion,
  RoleAccordionHeader,
  RoleTitle,
  RoleChevron,
  StaffList,
  StaffItem,
  StaffAvatar,
  StaffInfo,
  StaffName,
  StaffPhone,
  StaffDetailButton,
  PermissionTabsContainer,
  PermissionTabCell,
  SectionTitle,
  SectionTitleToggles,
  ToggleItem,
  ToggleLabel,
  PasswordChangeButton,
  PermissionCard,
  PermissionCardHeader,
  PermissionCardTitle,
  SelectAllRow,
  SelectAllLabel,
  PermissionRow,
  PermissionCell,
  PermissionClickArea,
  PermissionLabel,
  TealCheckbox,
  FooterBar,
  ResetButton,
  ApplyAllButton,
  SaveButton,
  PwModalContainer,
  PwModalHeader,
  PwModalHeaderTitle,
  PwModalCloseButton,
  PwModalBody,
  PwModalSection,
  PwModalSectionLabel,
  PwToggleRow,
  PwSwitch,
  PwToggleText,
  PwModalDivider,
  PwInput,
  PwVisibilityButton,
  PwModalFooter,
  ApplyAllConfirmContent,
  ApplyAllConfirmText,
  ApplyAllConfirmButtons,
  ApplyAllCancelButton,
  ApplyAllSubmitButton,
  HelpText,
  GuideButton,
  EmptyRightPanel,
  EmptyRightText,
} from './PermissionSettingsStyled';

// 한 줄에 보여줄 권한 항목 수 (Figma 기준 4칸)
const PERMISSIONS_PER_ROW = 4;

// 초성 검색 유틸
const CHOSUNG = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const getChosung = str =>
  [...(str || '')].map(c => {
    const code = c.charCodeAt(0) - 0xAC00;
    return code >= 0 && code <= 11171 ? CHOSUNG[Math.floor(code / 588)] : c;
  }).join('');
const isChosungOnly = str => [...str].every(c => CHOSUNG.includes(c));

// 숨길 권한 코드 (SUPERADMIN 전용 소메뉴 + 기타)
const HIDDEN_AUTH_CODES = [
  'ATTEND_EXCEL_DOWNLOAD', 'SCHEDULE_GOLF_MANAGE',
  'MEMBER_UNREGISTERED_VIEW', 'MEMBER_CORP_VIEW',
  'COMMUNITY_EVENT_VIEW', 'COMMUNITY_BANNER_VIEW',
];

// 세부 권한 전체를 숨길 메뉴 (PAGE_VIEW만 표시)
const HIDE_DETAIL_PREFIXES = [];

// 페이지 조회 권한 코드 (상단 분리 표시)
const PAGE_VIEW_SUFFIX = '_PAGE_VIEW';

// 소메뉴 접근 권한 코드
const SUBMENU_VIEW_CODES = [
  'MEMBER_UNREGISTERED_VIEW', 'MEMBER_GENERAL_VIEW', 'MEMBER_CORP_VIEW', 'MEMBER_TEMP_VIEW',
  'SCHEDULE_PRIVATE_VIEW', 'SCHEDULE_GROUP_VIEW',
  'TICKET_COURSE_VIEW', 'TICKET_PASS_VIEW', 'TICKET_TRIAL_VIEW',
  'COMMUNITY_CHAT_VIEW', 'COMMUNITY_NOTICE_VIEW', 'COMMUNITY_EVENT_VIEW',
  'COMMUNITY_BANNER_VIEW', 'COMMUNITY_TRANSFER_VIEW', 'COMMUNITY_SMS_VIEW',
  'EMPLOYEE_SETTING_VIEW', 'EMPLOYEE_SETTLEMENT_VIEW', 'EMPLOYEE_SALARY_SETTING_VIEW', 'EMPLOYEE_ATTENDANCE_MGMT_VIEW',
  'CENTER_STORE_VIEW', 'CENTER_PRODUCT_VIEW', 'CENTER_LOCKER_VIEW', 'CENTER_POINT_VIEW', 'CENTER_PERMISSION_VIEW',
];

// ============================================
// 비밀번호 설정 모달
// ============================================

const PasswordModal = ({ open, onClose, employee, token, onUpdate }) => {
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [switchPasswordYn, setSwitchPasswordYn] = useState('N');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (open && employee) {
      setPassword('');
      setConfirmPassword('');
      setSwitchPasswordYn(employee.switchPasswordYn || 'N');
      setShowPassword(false);
      setShowConfirm(false);
    }
  }, [open, employee]);

  const mutation = useMutation({
    mutationFn: () =>
      setEmployeePassword(token, employee.employeeId, {
        password: password || null,
      }),
    onSuccess: () => {
      toast.success('수정되었습니다');
      onClose();
    },
  });

  const handleSave = () => {
    if (!password.trim()) {
      toast.error('비밀번호를 입력해주세요');
      return;
    }
    if (password.length < 6) {
      toast.error('비밀번호는 6자리 이상이어야 합니다');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다');
      return;
    }
    mutation.mutate();
  };

  if (!employee) return null;

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <PwModalContainer>
        <PwModalHeader>
          <PwModalHeaderTitle>비밀번호 변경</PwModalHeaderTitle>
          <PwModalCloseButton onClick={onClose}>
            <Close />
          </PwModalCloseButton>
        </PwModalHeader>

        <PwModalBody>
          <PwModalSection>
            <PwModalSectionLabel>새 비밀번호</PwModalSectionLabel>
            <PwInput
              fullWidth
              type={showPassword ? 'text' : 'password'}
              placeholder="비밀번호를 입력해 주세요.(6자리 이상)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              InputProps={{
                endAdornment: (
                  <PwVisibilityButton onClick={() => setShowPassword(prev => !prev)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </PwVisibilityButton>
                ),
              }}
            />
            <PwInput
              fullWidth
              type={showConfirm ? 'text' : 'password'}
              placeholder="비밀번호 재입력"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              InputProps={{
                endAdornment: (
                  <PwVisibilityButton onClick={() => setShowConfirm(prev => !prev)}>
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </PwVisibilityButton>
                ),
              }}
            />
          </PwModalSection>
        </PwModalBody>

        <PwModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton
            variant="submit"
            onClick={handleSave}
            disabled={mutation.isPending}>
            저장
          </CommonModalButton>
        </PwModalFooter>
      </PwModalContainer>
    </Modal>
  );
};

// ============================================
// 메인 컴포넌트
// ============================================

const PermissionSettings = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  // 좌측 패널
  const [searchInput, setSearchInput] = useState('');
  const [expandedPositionIds, setExpandedPositionIds] = useState([]);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // 개별 직원 선택 시
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [excludeRetired, setExcludeRetired] = useState(true); // 퇴사자 제외

  // 우측 패널
  const [activeTabMenuId, setActiveTabMenuId] = useState(null);

  // 권한 체크 상태: { [menuAuthId]: boolean }
  const [checkedAuths, setCheckedAuths] = useState({});

  // 비밀번호 모달
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);

  // 전체 적용 확인 모달
  const [applyAllConfirmOpen, setApplyAllConfirmOpen] = useState(false);

  // 기본관리자 권한 수정 확인 모달
  const [defaultAdminSaveConfirmOpen, setDefaultAdminSaveConfirmOpen] = useState(false);

  // 토글 확인 모달
  const [toggleConfirm, setToggleConfirm] = useState(null); // { field, currentValue, label }

  // ============================================
  // API 1: 전체 직원 목록 조회 → 직급별 그룹핑
  // ============================================
  const { data: allEmployees = [] } = useQuery({
    queryKey: ['positionAuthAllEmployees', user?.headBranch?.headBranchId, user?.branch?.branchId],
    queryFn: async () => {
      const result = await getAllEmployees(user?.token, user?.headBranch?.headBranchId, user?.branch?.branchId);
      return result?.content || [];
    },
    enabled: !!user?.token && !!user?.headBranch?.headBranchId,
  });

  // 직급별 그룹핑 (대표 관리자 제외, 퇴사자 필터, sortOrder 정렬)
  const positions = useMemo(() => {
    const posMap = {};
    const posList = [];
    const filtered = allEmployees
      .filter(emp => emp.isManager !== 1)
      .filter(emp => !excludeRetired || emp.classification === 'WORKING');

    // 대표/기본 관리자 그룹
    const repAndDefault = filtered
      .filter(emp => emp.isRepresentative === 'Y' || emp.isDefaultAdmin === 'Y')
      .sort((a, b) => {
        if (a.isRepresentative === 'Y' && b.isRepresentative !== 'Y') return -1;
        if (a.isRepresentative !== 'Y' && b.isRepresentative === 'Y') return 1;
        return 0;
      });
    if (repAndDefault.length > 0) {
      posList.push({ positionId: '__rep__', positionName: '대표/기본 관리자', sortOrder: -1, employees: repAndDefault });
    }

    // 나머지 직급별 그룹
    filtered
      .filter(emp => emp.isRepresentative !== 'Y' && emp.isDefaultAdmin !== 'Y')
      .forEach(emp => {
        const posName = emp.positionName || '미지정';
        const posId = emp.positionId ?? 0;
        if (!posMap[posName]) {
          posMap[posName] = { positionId: posId, positionName: posName, sortOrder: emp.sortOrder ?? 99999, employees: [] };
          posList.push(posMap[posName]);
        }
        posMap[posName].employees.push(emp);
      });
    posList.sort((a, b) => a.sortOrder - b.sortOrder || (a.positionName || '').localeCompare(b.positionName || '', 'ko'));
    return posList;
  }, [allEmployees, excludeRetired]);


  // ============================================
  // API 3: 직급 권한 설정 조회
  // ============================================
  const { data: positionAuths = [] } = useQuery({
    queryKey: ['positionAuthAuths', selectedPositionId],
    queryFn: async () => {
      const result = await getPositionAuths(user?.token, selectedPositionId);
      return result?.content || [];
    },
    enabled: !!user?.token && selectedPositionId != null && !selectedEmployee,
    placeholderData: prev => prev,
  });

  // ============================================
  // API 4: 개별 직원 권한 조회
  // ============================================
  const { data: userAuths = [], placeholderData: previousUserAuths } = useQuery({
    queryKey: ['positionAuthUserAuths', selectedEmployee?.userId],
    queryFn: async () => {
      const result = await getUserAuths(user?.token, selectedEmployee.userId, user?.branch?.branchId);
      return result?.content || [];
    },
    enabled: !!user?.token && !!selectedEmployee?.userId,
    placeholderData: prev => prev,
  });

  // 현재 활성 권한 데이터 (직급 or 개별 직원)
  const activeAuths = selectedEmployee ? userAuths : positionAuths;

  // 권한 데이터를 menuId 기준으로 그룹핑 (menuAuthId 순 정렬)
  const { tabs, tabMap } = useMemo(() => {
    const tabList = [];
    const map = {};
    activeAuths.forEach(auth => {
      if (!map[auth.menuId]) {
        map[auth.menuId] = { menuId: auth.menuId, menuName: auth.menuName, items: [] };
        tabList.push(map[auth.menuId]);
      }
      map[auth.menuId].items.push(auth);
    });
    tabList.forEach(tab => {
      tab.items.sort((a, b) => (a.menuAuthId || 0) - (b.menuAuthId || 0));
    });
    return { tabs: tabList, tabMap: map };
  }, [activeAuths]);

  // 탭 이름 매핑 (menuName → 한글)
  const TAB_LABEL_MAP = {
    'Member management': '회원 관리',
    'Managing Attend History': '입장 내역 관리',
    'Schedule management': '스케줄 관리',
    'Community management': '커뮤니티 관리',
    'Employee Management': '임직원 관리',
    'Center management': '센터 관리',
    'Revenue Management': '매출 관리',
    'Voucher Management': '회원권 관리',
    'Manager App': '관리자 앱 관리',
  };

  const getTabLabel = menuName => TAB_LABEL_MAP[menuName] || menuName;

  // 권한 데이터가 바뀌면 체크 상태 초기화
  useEffect(() => {
    const newChecked = {};
    activeAuths.forEach(auth => {
      newChecked[auth.menuAuthId] = auth.authYn === 'Y';
    });
    setCheckedAuths(newChecked);
  }, [activeAuths]);

  // 첫 탭 자동 선택
  useEffect(() => {
    if (tabs.length > 0 && (activeTabMenuId == null || !tabMap[activeTabMenuId])) {
      setActiveTabMenuId(tabs[0].menuId);
    }
  }, [tabs, activeTabMenuId, tabMap]);

  // 현재 활성 탭의 권한 항목들 (숨길 항목 제외)
  const activeTabItemsRaw = tabMap[activeTabMenuId]?.items || [];
  const activeTabItemsFiltered = activeTabItemsRaw.filter(
    item => !HIDDEN_AUTH_CODES.includes(item.menuAuthCode),
  );

  // 페이지 조회 / 소메뉴 접근 / 세부 권한 분리
  const pageViewItem = activeTabItemsFiltered.find(
    item => item.menuAuthCode?.endsWith(PAGE_VIEW_SUFFIX),
  );
  const submenuViewItems = activeTabItemsFiltered.filter(
    item => SUBMENU_VIEW_CODES.includes(item.menuAuthCode),
  );
  const detailItems = activeTabItemsFiltered.filter(
    item =>
      !item.menuAuthCode?.endsWith(PAGE_VIEW_SUFFIX) &&
      !SUBMENU_VIEW_CODES.includes(item.menuAuthCode) &&
      !HIDE_DETAIL_PREFIXES.some(prefix => item.menuAuthCode?.startsWith(prefix)),
  );

  // 행 단위로 분할 (소메뉴)
  const submenuRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < submenuViewItems.length; i += PERMISSIONS_PER_ROW) {
      rows.push(submenuViewItems.slice(i, i + PERMISSIONS_PER_ROW));
    }
    return rows;
  }, [submenuViewItems]);

  // 행 단위로 분할 (세부 권한)
  const permissionRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < detailItems.length; i += PERMISSIONS_PER_ROW) {
      rows.push(detailItems.slice(i, i + PERMISSIONS_PER_ROW));
    }
    return rows;
  }, [detailItems]);

  // 전체 선택 여부 (세부 권한만)
  const isAllSelected =
    detailItems.length > 0 && detailItems.every(item => checkedAuths[item.menuAuthId]);

  // 선택된 직급 라벨
  const selectedPositionLabel = useMemo(
    () => positions.find(p => p.positionId === selectedPositionId)?.positionName || '',
    [positions, selectedPositionId],
  );

  // 섹션 타이틀 (직급명 - 직원명(전화번호))
  const sectionTitle = useMemo(() => {
    if (selectedEmployee) {
      const positionName = selectedEmployee.positionName || '미지정';
      return `${positionName} - ${selectedEmployee.name}(${selectedEmployee.phoneNumber})`;
    }
    return '';
  }, [selectedEmployee]);

  // 검색 필터
  const trimmedSearch = searchInput.trim().toLowerCase();

  // ============================================
  // 핸들러
  // ============================================

  const handlePositionToggle = positionId => {
    setExpandedPositionIds(prev =>
      prev.includes(positionId)
        ? prev.filter(id => id !== positionId)
        : [...prev, positionId],
    );
  };

  const handleEmployeeSelect = employee => {
    setSelectedEmployee(employee);
    setSelectedPositionId(employee.positionId ?? 0);
  };

  const handleBackToPosition = () => {
    setSelectedEmployee(null);
  };

  const TOGGLE_LABELS = {
    managerAppLoginYn: '관리자앱 로그인 허용',
    switchPasswordYn: '관리자 변경 시 비밀번호 확인',
    isRepresentative: '대표 관리자 계정으로 설정',
  };

  const handleToggleEmployeeSettingConfirm = (field, currentValue) => {
    setToggleConfirm({ field, currentValue, label: TOGGLE_LABELS[field] || field });
  };

  const executeToggleEmployeeSetting = async () => {
    if (!toggleConfirm || !selectedEmployee) return;
    const { field, currentValue } = toggleConfirm;
    const newValue = currentValue === 'Y' ? 'N' : 'Y';
    setToggleConfirm(null);
    try {
      await setEmployeePassword(user?.token, selectedEmployee.employeeId, {
        [field]: newValue,
        branchId: user?.branch?.branchId,
      });
      setSelectedEmployee(prev => ({ ...prev, [field]: newValue }));
      queryClient.invalidateQueries({ queryKey: ['positionAuthAllEmployees'] });
      // 대표관리자 설정 시 전체 권한이 부여되므로 권한 체크박스도 갱신
      if (field === 'isRepresentative') {
        queryClient.invalidateQueries({ queryKey: ['positionAuthUserAuths', selectedEmployee.userId] });
      }
      toast.success('수정되었습니다');
    } catch (err) {
      toast.error('수정에 실패했습니다');
    }
  };

  const handlePermissionToggle = menuAuthId => {
    setCheckedAuths(prev => {
      const updated = { ...prev, [menuAuthId]: !prev[menuAuthId] };
      if (pageViewItem) {
        // 대메뉴 접근 권한 해제 → 소메뉴 + 세부 권한 전부 해제
        if (menuAuthId === pageViewItem.menuAuthId && !updated[menuAuthId]) {
          submenuViewItems.forEach(item => {
            updated[item.menuAuthId] = false;
          });
          detailItems.forEach(item => {
            updated[item.menuAuthId] = false;
          });
        }
        // 소메뉴 또는 세부 권한 하나라도 체크 → 대메뉴 접근 권한 자동 체크
        if (menuAuthId !== pageViewItem.menuAuthId) {
          const hasAny = submenuViewItems.some(item => updated[item.menuAuthId]) ||
            detailItems.some(item => updated[item.menuAuthId]);
          if (hasAny) {
            updated[pageViewItem.menuAuthId] = true;
          }
        }
      }
      return updated;
    });
  };

  const handleSelectAll = () => {
    const next = !isAllSelected;
    setCheckedAuths(prev => {
      const updated = { ...prev };
      detailItems.forEach(item => {
        updated[item.menuAuthId] = next;
      });
      // 세부 권한 하나라도 체크되면 메뉴 접근 권한도 자동 체크
      if (next && pageViewItem) {
        updated[pageViewItem.menuAuthId] = true;
      }
      return updated;
    });
  };

  const handleLockClick = (e, employee) => {
    e.stopPropagation();
    setPasswordTarget(employee);
    setPasswordModalOpen(true);
  };

  // ============================================
  // Mutations (API 5, 6, 7)
  // ============================================

  const isDefaultAdminSelected = selectedEmployee?.isDefaultAdmin === 'Y';

  const performLogout = () => {
    queryClient.clear();
    localStorage.removeItem('pila-user');
    localStorage.removeItem('user-menu');
    localStorage.removeItem('pila-employee-cache');
    window.location.replace('/');
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const checkedIds = Object.entries(checkedAuths)
        .filter(([, v]) => v)
        .map(([id]) => id)
        .join(',');
      if (selectedEmployee) {
        return saveUserAuths(user?.token, selectedEmployee.userId, checkedIds, user?.branch?.branchId);
      }
      return savePositionAuths(user?.token, selectedPositionId, checkedIds, user?.branch?.branchId);
    },
    onSuccess: () => {
      if (isDefaultAdminSelected) {
        toast.success('설정이 저장되었습니다. 로그아웃됩니다.');
        setTimeout(performLogout, 500);
      } else {
        toast.success('설정이 저장되었습니다');
        if (selectedEmployee) {
          queryClient.invalidateQueries({ queryKey: ['positionAuthUserAuths', selectedEmployee.userId] });
        } else {
          queryClient.invalidateQueries({ queryKey: ['positionAuthAuths', selectedPositionId] });
        }
      }
    },
  });

  const applyAllMutation = useMutation({
    mutationFn: async () => {
      const checkedIds = Object.entries(checkedAuths)
        .filter(([, v]) => v)
        .map(([id]) => id)
        .join(',');
      // 1. 직급 권한 저장
      await savePositionAuths(user?.token, selectedPositionId, checkedIds, user?.branch?.branchId);
      // 2. 해당 직급 전체 직원에게 bulk 적용
      const currentPositionEmployees = positions.find(p => p.positionId === selectedPositionId)?.employees || [];
      const userIds = currentPositionEmployees.map(e => e.userId).join(',');
      if (!userIds) return { appliedCount: 0 };
      return await bulkSaveUserAuths(user?.token, userIds, checkedIds, user?.branch?.branchId);
    },
    onSuccess: res => {
      toast.success(
        `${selectedPositionLabel} 전체에 적용되었습니다 (${res?.appliedCount || 0}명)`,
      );
      queryClient.invalidateQueries({ queryKey: ['positionAuthAuths', selectedPositionId] });
      // 개별 직원 권한 캐시도 전부 무효화 (직원 클릭 시 최신 데이터 반영)
      queryClient.invalidateQueries({ queryKey: ['positionAuthUserAuths'] });
    },
  });

  // 초기화: API 원본 데이터로 체크박스 되돌리기
  const handleReset = () => {
    const original = {};
    activeAuths.forEach(auth => {
      original[auth.menuAuthId] = auth.authYn === 'Y';
    });
    setCheckedAuths(original);
    toast.success('초기화되었습니다');
  };

  // ============================================
  // 렌더
  // ============================================

  // 탭 그리드: 4칸×2줄 = 8슬롯, 빈 칸은 placeholder
  const TAB_GRID_COLS = 4;
  const TAB_GRID_ROWS = 2;
  const totalSlots = TAB_GRID_COLS * TAB_GRID_ROWS;
  const emptySlots = Math.max(0, totalSlots - tabs.length);

  return (
    <PageContainer>
      {/* ===== 좌측: 검색 + 직급 아코디언 ===== */}
      <LeftColumn>
        <SearchCard style={{ flexDirection: 'column', height: 'auto', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
            <SearchInput
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="이름, 전화번호, 초성 검색"
              autoComplete="off"
              inputProps={{ autoComplete: 'off' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: '#A6A6A6', fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <ExcludeRetiredRow onClick={() => setExcludeRetired(prev => !prev)}>
              <Switch checked={excludeRetired} size="small"
                sx={{
                  width: 36, height: 20, padding: 0,
                  '& .MuiSwitch-switchBase': { padding: '2px',
                    '&.Mui-checked': { transform: 'translateX(16px)', color: '#fff',
                      '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 } } },
                  '& .MuiSwitch-thumb': { width: 16, height: 16 },
                  '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#D9D9D9', opacity: 1 },
                }} />
              <ExcludeRetiredLabel>퇴사자 제외</ExcludeRetiredLabel>
            </ExcludeRetiredRow>
            <GuideButton onClick={() => setHelpModalOpen(true)}>
              📖 권한 관리 가이드
            </GuideButton>
          </div>
        </SearchCard>

        <RoleListWrapper>
          {positions.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', color: '#999', fontSize: '14px' }}>
              등록된 직원이 없습니다.
            </div>
          )}
          {positions.map(pos => {
            const isExpanded = expandedPositionIds.includes(pos.positionId);

            // 검색 필터 적용 (이름, 전화번호, 초성)
            const filteredEmployees = trimmedSearch
              ? pos.employees.filter(emp => {
                  const normalize = v => (v || '').toLowerCase().replace(/-/g, '');
                  const name = (emp.name || '').toLowerCase();
                  const phone = normalize(emp.phoneNumber);
                  if (name.includes(trimmedSearch) || phone.includes(trimmedSearch)) return true;
                  if (isChosungOnly(trimmedSearch) && getChosung(emp.name).includes(trimmedSearch)) return true;
                  return false;
                })
              : pos.employees;

            // 검색 중이면 매칭 결과가 있는 직급은 자동 펼침
            const shouldExpand = trimmedSearch ? filteredEmployees.length > 0 : isExpanded;

            return (
              <RoleAccordion key={pos.positionId} expanded={shouldExpand}>
                <RoleAccordionHeader onClick={() => handlePositionToggle(pos.positionId)}>
                  <RoleTitle>
                    {pos.positionName}
                    {pos.employees.length > 0 && (
                      <span style={{ color: '#A6A6A6', fontSize: 14, marginLeft: 8 }}>
                        ({pos.employees.length}명)
                      </span>
                    )}
                  </RoleTitle>
                  <RoleChevron>
                    {shouldExpand ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </RoleChevron>
                </RoleAccordionHeader>
                {shouldExpand && filteredEmployees.length > 0 && (
                  <StaffList>
                    {filteredEmployees.map(emp => (
                      <StaffItem
                        key={emp.employeeId}
                        selected={selectedEmployee?.employeeId === emp.employeeId}
                        onClick={() => handleEmployeeSelect(emp)}>
                        <StaffAvatar src={emp.profileImage || undefined} />
                        <StaffInfo>
                          <StaffName>
                            {emp.name}
                            {emp.isRepresentative === 'Y' && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#4AB3BC', borderRadius: '4px', padding: '1px 6px' }}>대표 관리자</span>}
                            {emp.isDefaultAdmin === 'Y' && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#888', borderRadius: '4px', padding: '1px 6px' }}>기본 관리자</span>}
                          </StaffName>
                          <StaffPhone>({emp.phoneNumber})</StaffPhone>
                        </StaffInfo>
                        {/* <StaffDetailButton
                          onClick={e => {
                            e.stopPropagation();
                            handleEmployeeSelect(emp);
                          }}>
                          임직원 상세
                        </StaffDetailButton> */}
                      </StaffItem>
                    ))}
                  </StaffList>
                )}
              </RoleAccordion>
            );
          })}
        </RoleListWrapper>
      </LeftColumn>

      {/* ===== 우측: 권한 카테고리 탭 + 매트릭스 + 액션 ===== */}
      <RightColumn>
        {!selectedEmployee ? (
          <EmptyRightPanel>
            <EmptyRightText>좌측에서 회원을 선택해주세요</EmptyRightText>
          </EmptyRightPanel>
        ) : (
          <>
            <PermissionTabsContainer>
              {tabs.map(tab => (
                <PermissionTabCell
                  key={tab.menuId}
                  active={activeTabMenuId === tab.menuId}
                  onClick={() => setActiveTabMenuId(tab.menuId)}>
                  {getTabLabel(tab.menuName)}
                </PermissionTabCell>
              ))}
              {Array.from({ length: emptySlots }).map((_, i) => (
                <PermissionTabCell key={`empty-${i}`} empty>
                  placeholder
                </PermissionTabCell>
              ))}
            </PermissionTabsContainer>

            <SectionTitle>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>{sectionTitle}</span>
              {selectedEmployee && selectedEmployee.isDefaultAdmin !== 'Y' && (
                <SectionTitleToggles>
                  <ToggleItem onClick={() => handleToggleEmployeeSettingConfirm('managerAppLoginYn', selectedEmployee.managerAppLoginYn)}>
                    <TealCheckbox checked={selectedEmployee.managerAppLoginYn === 'Y'} disableRipple />
                    <ToggleLabel>관리자앱 로그인 허용</ToggleLabel>
                  </ToggleItem>
                  <ToggleItem onClick={() => handleToggleEmployeeSettingConfirm('isRepresentative', selectedEmployee.isRepresentative)}>
                    <TealCheckbox checked={selectedEmployee.isRepresentative === 'Y'} disableRipple />
                    <ToggleLabel>대표 관리자 계정으로 설정</ToggleLabel>
                  </ToggleItem>
                </SectionTitleToggles>
              )}
            </SectionTitle>

            {/* 좌측 대메뉴 접근 권한 */}
            {pageViewItem && (
              <PermissionCard>
                <PermissionCardHeader>
                  <PermissionCardTitle>좌측 대메뉴 접근 권한</PermissionCardTitle>
                </PermissionCardHeader>
                <PermissionRow>
                  <PermissionCell>
                    <PermissionClickArea onClick={() => handlePermissionToggle(pageViewItem.menuAuthId)}>
                      <TealCheckbox checked={!!checkedAuths[pageViewItem.menuAuthId]} disableRipple />
                      <PermissionLabel>
                        {tabMap[activeTabMenuId]
                          ? getTabLabel(tabMap[activeTabMenuId].menuName)
                          : ''}
                      </PermissionLabel>
                    </PermissionClickArea>
                  </PermissionCell>
                </PermissionRow>
              </PermissionCard>
            )}

            {/* 좌측 소메뉴 접근 권한 */}
            <PermissionCard>
                <PermissionCardHeader>
                  <PermissionCardTitle>좌측 소메뉴 접근 권한</PermissionCardTitle>
                </PermissionCardHeader>
                {submenuRows.map((row, rowIdx) => (
                  <PermissionRow key={rowIdx}>
                    {row.map(item => (
                      <PermissionCell key={item.menuAuthId}>
                        <PermissionClickArea onClick={() => handlePermissionToggle(item.menuAuthId)}>
                          <TealCheckbox checked={!!checkedAuths[item.menuAuthId]} disableRipple />
                          <PermissionLabel>{item.menuAuthName}</PermissionLabel>
                        </PermissionClickArea>
                      </PermissionCell>
                    ))}
                    {row.length < PERMISSIONS_PER_ROW &&
                      Array.from({ length: PERMISSIONS_PER_ROW - row.length }).map((_, i) => (
                        <PermissionCell key={`empty-${i}`} />
                      ))}
                  </PermissionRow>
                ))}
              {submenuViewItems.length === 0 && (
                <PermissionRow style={{ justifyContent: 'center', padding: '32px 24px' }}>
                  <PermissionLabel style={{ color: '#999' }}>소메뉴가 없습니다</PermissionLabel>
                </PermissionRow>
              )}
              </PermissionCard>

            {/* 세부 권한 */}
            <PermissionCard>
              <PermissionCardHeader>
                <PermissionCardTitle>세부 권한</PermissionCardTitle>
                {detailItems.length > 0 && (
                  <SelectAllRow onClick={handleSelectAll}>
                    <SelectAllLabel>전체선택</SelectAllLabel>
                    <TealCheckbox checked={isAllSelected} large disableRipple />
                  </SelectAllRow>
                )}
              </PermissionCardHeader>
              {detailItems.length === 0 ? (
                <PermissionRow style={{ justifyContent: 'center', padding: '32px 24px' }}>
                  <PermissionLabel style={{ color: '#999' }}>세부 권한이 없습니다</PermissionLabel>
                </PermissionRow>
              ) : (
                permissionRows.map((row, rowIdx) => (
                  <PermissionRow key={rowIdx}>
                    {row.map(item => (
                      <PermissionCell key={item.menuAuthId}>
                        <PermissionClickArea onClick={() => handlePermissionToggle(item.menuAuthId)}>
                          <TealCheckbox checked={!!checkedAuths[item.menuAuthId]} disableRipple />
                          <PermissionLabel>
                            {item.menuAuthName}
                          </PermissionLabel>
                        </PermissionClickArea>
                      </PermissionCell>
                    ))}
                    {row.length < PERMISSIONS_PER_ROW &&
                      Array.from({ length: PERMISSIONS_PER_ROW - row.length }).map((_, i) => (
                        <PermissionCell key={`empty-${i}`} />
                      ))}
                  </PermissionRow>
                ))
              )}
            </PermissionCard>

            <FooterBar>
              {(
                <ResetButton onClick={handleReset}>
                  초기화
                </ResetButton>
              )}
              {(
                <ApplyAllButton
                  onClick={() => setApplyAllConfirmOpen(true)}
                  disabled={applyAllMutation.isPending}>
                  해당직급 전체 적용
                </ApplyAllButton>
              )}
              <SaveButton
                onClick={() => {
                  if (isDefaultAdminSelected) {
                    setDefaultAdminSaveConfirmOpen(true);
                  } else {
                    saveMutation.mutate();
                  }
                }}
                disabled={saveMutation.isPending}>
                설정 저장
              </SaveButton>
            </FooterBar>
          </>
        )}
      </RightColumn>

      {/* 비밀번호 설정 모달 */}
      <PasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        employee={passwordTarget}
        token={user?.token}
        onUpdate={updated => {
          if (selectedEmployee?.employeeId === updated.employeeId) {
            setSelectedEmployee(prev => ({ ...prev, switchPasswordYn: updated.switchPasswordYn }));
          }
          queryClient.invalidateQueries({ queryKey: ['positionAuthAllEmployees'] });
        }}
      />

      {/* 전체 적용 확인 모달 */}
      <Modal open={applyAllConfirmOpen} onClose={() => setApplyAllConfirmOpen(false)} disableScrollLock>
        <ApplyAllConfirmContent>
          <ApplyAllConfirmText>
            <span style={{ color: '#4AB3BC', fontWeight: 700 }}>해당 직급</span>에 설정한 권한을
            <br />전체 적용하시겠습니까?
          </ApplyAllConfirmText>
          <ApplyAllConfirmButtons>
            <ApplyAllCancelButton onClick={() => setApplyAllConfirmOpen(false)}>
              닫기
            </ApplyAllCancelButton>
            <ApplyAllSubmitButton
              disabled={applyAllMutation.isPending}
              onClick={() => {
                setApplyAllConfirmOpen(false);
                applyAllMutation.mutate();
              }}>
              확인
            </ApplyAllSubmitButton>
          </ApplyAllConfirmButtons>
        </ApplyAllConfirmContent>
      </Modal>

      {/* 기본관리자 권한 수정 확인 모달 */}
      <Modal open={defaultAdminSaveConfirmOpen} onClose={() => setDefaultAdminSaveConfirmOpen(false)} disableScrollLock>
        <ApplyAllConfirmContent>
          <ApplyAllConfirmText>
            <span style={{ color: '#4AB3BC', fontWeight: 700 }}>기본 관리자</span>의 권한을 수정하면
            <br />재로그인이 필요합니다.
            <br /><br />
            <span style={{ fontSize: '14px', color: '#999' }}>저장 후 자동으로 로그아웃됩니다.</span>
          </ApplyAllConfirmText>
          <ApplyAllConfirmButtons>
            <ApplyAllCancelButton onClick={() => setDefaultAdminSaveConfirmOpen(false)}>
              취소
            </ApplyAllCancelButton>
            <ApplyAllSubmitButton
              disabled={saveMutation.isPending}
              onClick={() => {
                setDefaultAdminSaveConfirmOpen(false);
                saveMutation.mutate();
              }}>
              확인
            </ApplyAllSubmitButton>
          </ApplyAllConfirmButtons>
        </ApplyAllConfirmContent>
      </Modal>

      {/* 토글 확인 모달 */}
      <Modal open={!!toggleConfirm} onClose={() => setToggleConfirm(null)} disableScrollLock>
        <ApplyAllConfirmContent>
          <ApplyAllConfirmText>
            <span style={{ color: '#4AB3BC', fontWeight: 700 }}>{toggleConfirm?.label}</span>을
            <br />{toggleConfirm?.currentValue === 'Y' ? '해제' : '설정'}하시겠습니까?
            {toggleConfirm?.field === 'isRepresentative' && toggleConfirm?.currentValue !== 'Y' && (
              <br />
            )}
            {toggleConfirm?.field === 'isRepresentative' && toggleConfirm?.currentValue !== 'Y' && (
              <span style={{ fontSize: '15px', color: '#999', marginTop: '4px' }}>* 지점당 1명만 설정 가능, 모든 권한 부여</span>
            )}
          </ApplyAllConfirmText>
          <ApplyAllConfirmButtons>
            <ApplyAllCancelButton onClick={() => setToggleConfirm(null)}>
              닫기
            </ApplyAllCancelButton>
            <ApplyAllSubmitButton onClick={executeToggleEmployeeSetting}>
              확인
            </ApplyAllSubmitButton>
          </ApplyAllConfirmButtons>
        </ApplyAllConfirmContent>
      </Modal>
      {/* 도움말 모달 */}
      <Modal open={helpModalOpen} onClose={() => setHelpModalOpen(false)} disableScrollLock>
        <PwModalContainer>
          <PwModalHeader>
            <PwModalHeaderTitle>권한 관리 가이드</PwModalHeaderTitle>
            <PwModalCloseButton onClick={() => setHelpModalOpen(false)}>
              <Close />
            </PwModalCloseButton>
          </PwModalHeader>
          <PwModalBody style={{ gap: '20px' }}>
            <PwModalSection>
              <PwModalSectionLabel>🔑 권한 관리란?</PwModalSectionLabel>
              <HelpText>직원별로 접근 가능한 메뉴와 기능을 설정합니다. 좌측에서 직원을 선택한 후, 우측에서 권한을 체크/해제하고 저장하면 됩니다.</HelpText>
            </PwModalSection>
            <PwModalDivider />
            <PwModalSection>
              <PwModalSectionLabel>⭐ 대표 관리자</PwModalSectionLabel>
              <HelpText>지점당 1명만 설정 가능합니다. 설정 시 모든 권한이 자동으로 부여됩니다. 관리자 변경 모달에서 대표/기본 관리자 섹션에 표시됩니다.</HelpText>
            </PwModalSection>
            <PwModalDivider />
            <PwModalSection>
              <PwModalSectionLabel>👤 기본 관리자</PwModalSectionLabel>
              <HelpText>센터 생성 시 자동으로 만들어지는 시스템 계정입니다. 첫 생성 시 모든 권한이 부여되므로, 비노출을 원하는 권한은 꼭 설정해주시길 바랍니다. 로그인 화면에서 로그인 시 기본 관리자의 권한으로 접속됩니다.</HelpText>
            </PwModalSection>
            <PwModalDivider />
            <PwModalSection>
              <PwModalSectionLabel>📋 권한 구조</PwModalSectionLabel>
              <HelpText>
                • <strong>대메뉴 접근 권한</strong> — 좌측 메뉴 카테고리 접근 여부<br />
                • <strong>소메뉴 접근 권한</strong> — 하위 메뉴별 접근 여부<br />
                • <strong>세부 권한</strong> — 버튼/기능별 사용 여부<br /><br />
                대메뉴를 해제하면 소메뉴와 세부 권한이 모두 해제됩니다.
              </HelpText>
            </PwModalSection>
          </PwModalBody>
        </PwModalContainer>
      </Modal>
    </PageContainer>
  );
};

export default PermissionSettings;
